import { z } from "zod/v4";

import {
  cartTargetingRefinementMessages,
  cartTargetingRefinements,
} from "./cart-targeting.refinements";
import type { BundleDiscount } from "./bundle-discount";
import type { CreateComponentDto } from "./components";
import {
  upsellProductRulesRefinementMessages,
  upsellProductRulesRefinements,
} from "./upsell-product-rules.refinements";

export const componentRefinementMessages = {
  actionExpressionQueriesHaveTypeOrValue:
    upsellProductRulesRefinementMessages.expressionQueriesHaveTypeOrValue,
  cartTargetingExpressionQueriesHaveTypeOrValue:
    cartTargetingRefinementMessages.expressionQueriesHaveTypeOrValue,
  bundleTiersRequired: "A bundle discount needs at least one tier.",
  bundleTierThresholdWithinLimit:
    "A discount tier can't require more products than the bundle shows.",
  bundleTierThresholdsUnique:
    "Two discount tiers can't require the same number of products.",
  bundleTierValuePositive: "Every discount tier needs a value above 0.",
  bundleTierPercentageMax: "A percentage discount tier can't exceed 100.",
};

export function refineBundleDiscount(
  {
    discount,
    maxProductLimit,
  }: {
    discount?: BundleDiscount | null;
    maxProductLimit: number;
  },
  ctx: z.RefinementCtx,
  discountPath: (string | number)[]
): void {
  if (!discount || discount.discountType === "none") {
    return;
  }

  const issue = (message: string, path: (string | number)[] = []) =>
    ctx.addIssue({
      code: "custom",
      path: [...discountPath, "discountTiers", ...path],
      message,
    });

  if (discount.discountTiers.length === 0) {
    issue(componentRefinementMessages.bundleTiersRequired);
    return;
  }

  const thresholds = discount.discountTiers.map((tier) => tier.minimumProducts);

  if (thresholds.some((threshold) => threshold > maxProductLimit)) {
    issue(componentRefinementMessages.bundleTierThresholdWithinLimit);
  }

  if (new Set(thresholds).size !== thresholds.length) {
    issue(componentRefinementMessages.bundleTierThresholdsUnique);
  }

  discount.discountTiers.forEach((tier, index) => {
    if (tier.value <= 0) {
      issue(componentRefinementMessages.bundleTierValuePositive, [
        index,
        "value",
      ]);
    } else if (discount.discountType === "percentage" && tier.value > 100) {
      issue(componentRefinementMessages.bundleTierPercentageMax, [
        index,
        "value",
      ]);
    }
  });
}

// Base DTOs stay loose; the write-time value-required checks are layered on
// here so they gate the writers that opt in — today, the experience save path
// (put-experience). A dedicated FE form would apply the same refinements once
// it exists.
export function applyComponentRefinements<
  S extends z.ZodType<CreateComponentDto>,
>(schema: S) {
  return schema.superRefine((component, ctx) => {
    component.config.rules.forEach((rule, index) => {
      if (
        !upsellProductRulesRefinements.expressionQueriesHaveTypeOrValue({
          action: { expression: rule.action },
        })
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["config", "rules", index, "action"],
          message:
            componentRefinementMessages.actionExpressionQueriesHaveTypeOrValue,
        });
      }

      if (
        rule.targeting.type === "cart" &&
        !cartTargetingRefinements.expressionQueriesHaveTypeOrValue({
          expression: rule.targeting.expression,
        })
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["config", "rules", index, "targeting", "expression"],
          message:
            componentRefinementMessages.cartTargetingExpressionQueriesHaveTypeOrValue,
        });
      }
    });

    if (component.config.type === "bundleOnsiteUpsell") {
      refineBundleDiscount(component.config, ctx, ["config", "discount"]);
    }
  });
}
